/**
 * @file packages/utils/src/text/highlightSegmentProcessor.ts
 * @description Provides utilities for processing text and PDF data to generate highlighting information.
 */
import { parseTranslationResponse } from '../cache/translationCacheUtils.js';
/**
 * Converts a backend TextSelectionTranslation object into a UI-friendly
 * UiHighlightTooltipData object.
 *
 * This function is shared between text and PDF highlighting systems to ensure
 * consistent tooltip data format across the application.
 *
 * @param translation The translation object from the database.
 * @returns A tooltip data object, or undefined if the input is invalid.
 */
export function convertTranslationToTooltipData(translation) {
    if (!translation || typeof translation.orderId !== 'number')
        return undefined;
    let displayTranslatedText = '';
    if (typeof translation.translatedText === 'string') {
        displayTranslatedText = translation.translatedText;
    }
    else if (translation.translatedText &&
        typeof translation.translatedText.translatedText === 'string') {
        displayTranslatedText = translation.translatedText.translatedText;
    }
    else {
        // Attempt to use parseTranslationResponse for more complex TranslatedTextResult objects
        try {
            const parsed = parseTranslationResponse(translation);
            displayTranslatedText = parsed.translatedText;
        }
        catch (e) {
            displayTranslatedText = 'Translation not available';
            console.error('Error in convertTranslationToTooltipData while parsing translatedText:', e);
        }
    }
    return {
        id: translation.orderId,
        originalText: translation.originalText,
        displayTranslatedText: displayTranslatedText,
        sourceLanguage: translation.sourceLanguage,
        targetLanguage: translation.targetLanguage,
        provider: translation.provider,
        model: translation.model,
    };
}
/**
 * Generates highlighted segments for text-based content by splitting text based on translations.
 *
 * @param text The original paragraph text.
 * @param textTranslations An array of translation records that fall on this paragraph.
 * @returns An array of HighlightSegment objects.
 */
export function generateTextHighlightedSegments(text, textTranslations) {
    // Filter for valid text-only translations
    const validWords = textTranslations.filter((t) => t.location.type === 'text');
    if (!text || validWords.length === 0) {
        return [{ text, isTranslated: false }];
    }
    // 1. Collect all boundary points from translation records
    const points = new Set([0, text.length]);
    validWords.forEach(word => {
        // Access offsets from the new 'location' object
        points.add(word.location.startOffset);
        points.add(word.location.endOffset);
    });
    // 2. Sort points to create intervals
    const sortedPoints = Array.from(points).sort((a, b) => a - b);
    const resultingSegments = [];
    // 3. Iterate over intervals
    for (let i = 0; i < sortedPoints.length - 1; i++) {
        const intervalStart = sortedPoints[i];
        const intervalEnd = sortedPoints[i + 1];
        if (intervalStart >= intervalEnd)
            continue;
        const intervalText = text.substring(intervalStart, intervalEnd);
        if (!intervalText)
            continue;
        // 4. Find the translation that "best" covers this interval (smallest containing range)
        const containingWords = validWords.filter(word => {
            const { startOffset, endOffset } = word.location;
            return startOffset <= intervalStart && endOffset >= intervalEnd;
        });
        if (containingWords.length > 0) {
            const bestWord = containingWords.reduce((smallest, current) => {
                const rangeSmallest = smallest.location.endOffset - smallest.location.startOffset;
                const rangeCurrent = current.location.endOffset - current.location.startOffset;
                return rangeCurrent < rangeSmallest ? current : smallest;
            });
            resultingSegments.push({
                text: intervalText,
                isTranslated: true,
                translationData: bestWord,
            });
        }
        else {
            resultingSegments.push({
                text: intervalText,
                isTranslated: false,
            });
        }
    }
    return resultingSegments;
}
/**
 * Builds a data structure containing precise highlighting information for PDF words.
 *
 * @param pdfTranslations An array of all translation records for the PDF document.
 * @param getWordTextLength A function to get the length of a word by its ID (for calculations).
 * @param getParagraphWords Optional function to get word order for a paragraph (needed for multi-word selections).
 * @returns A Map where the key is a wordId and the value contains an array of character-level highlight info.
 */
export function buildPdfHighlightData(pdfTranslations, getWordTextLength, getParagraphWords) {
    const highlightMap = new Map();
    const validPdfTranslations = pdfTranslations.filter((t) => t.location.type === 'pdf');
    for (const translation of validPdfTranslations) {
        const { startWordId, endWordId, startWordOffset, endWordOffset } = translation.location;
        const translationId = `${translation.orderId}-${startWordId}-${endWordId}`;
        if (startWordId === endWordId) {
            // --- Single-word selection ---
            const existingHighlights = highlightMap.get(startWordId) || [];
            existingHighlights.push({
                id: translationId,
                isFullyHighlighted: false, // It's a partial highlight
                startOffset: startWordOffset,
                endOffset: endWordOffset,
                translationData: translation,
            });
            highlightMap.set(startWordId, existingHighlights);
        }
        else {
            // --- Multi-word selection ---
            const startWordLength = getWordTextLength(startWordId);
            if (startWordLength !== undefined) {
                const existingStartHighlights = highlightMap.get(startWordId) || [];
                existingStartHighlights.push({
                    id: translationId,
                    isFullyHighlighted: startWordOffset === 0,
                    startOffset: startWordOffset,
                    endOffset: startWordLength, // Highlight to the end of the start word
                    translationData: translation,
                });
                highlightMap.set(startWordId, existingStartHighlights);
            }
            // Mark the end word
            const existingEndHighlights = highlightMap.get(endWordId) || [];
            existingEndHighlights.push({
                id: translationId,
                isFullyHighlighted: endWordOffset === getWordTextLength(endWordId),
                startOffset: 0, // Highlight from the beginning of the end word
                endOffset: endWordOffset,
                translationData: translation,
            });
            highlightMap.set(endWordId, existingEndHighlights);
            // --- Handle middle words in multi-word selections ---
            // Use the optional getParagraphWords function to get word order information
            if (getParagraphWords) {
                const words = getParagraphWords(translation.location.paragraphId);
                if (words) {
                    const startIndex = words.findIndex(w => w.id === startWordId);
                    const endIndex = words.findIndex(w => w.id === endWordId);
                    if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
                        // Mark all words between start and end as fully highlighted
                        for (let i = startIndex + 1; i < endIndex; i++) {
                            const middleWordId = words[i].id;
                            const existingMiddleHighlights = highlightMap.get(middleWordId) || [];
                            existingMiddleHighlights.push({
                                id: translationId,
                                isFullyHighlighted: true,
                                translationData: translation,
                            });
                            highlightMap.set(middleWordId, existingMiddleHighlights);
                        }
                    }
                }
            }
        }
    }
    return highlightMap;
}
//# sourceMappingURL=highlightSegmentProcessor.js.map